import { useEffect, useRef, useState } from 'react';

type Stat = { label: string; value: string };
type Bar = { label: string; pct: number };

type Reason = {
  num: string;
  icon: string;
  accent: string;
  accentBg: string;
  accentBorder: string;
  title: string;
  hook: string;
  body: string;
  label: string;
  valueNum: number;
  valuePrefix: string;
  valueSuffix: string;
  tags: string[];
  stats: Stat[];
  bar: Bar;
  dataLeft: boolean;
};

const REASONS: Reason[] = [
  {
    num: '01',
    icon: 'ri-crosshair-2-line',
    accent: '#7c3aed',
    accentBg: 'bg-violet-50',
    accentBorder: 'border-violet-100',
    title: 'Tăng độ chính xác giao dịch',
    hook: 'Một con số sai có thể khiến bạn trả giá đắt.',
    body:
      'Giao dịch ngoại hối vận hành trên hàng loạt phép tính phức tạp: khối lượng lệnh, đòn bẩy, ký quỹ và giá trị pip. Chỉ một sai lệch nhỏ khi tính tay cũng đủ khiến vị thế lệch khỏi kế hoạch. Máy tính Forex tự động hóa toàn bộ quy trình, loại bỏ sai số thủ công và trả về kết quả chính xác đến từng chữ số thập phân trong tích tắc.',
    label: 'Độ chính xác - 0 sai số',
    valueNum: 100,
    valuePrefix: '',
    valueSuffix: '%',
    tags: ['Khối lượng lệnh', 'Đòn bẩy', 'Ký quỹ', 'Giá trị pip'],
    stats: [
      { label: 'Sai số', value: '0.00%' },
      { label: 'Đơn vị', value: '0.1 pip' },
      { label: 'Tốc độ', value: '<0.1s' },
    ],
    bar: { label: 'Độ tin cậy tính toán', pct: 100 },
    dataLeft: false,
  },
  {
    num: '02',
    icon: 'ri-shield-check-line',
    accent: '#7c3aed',
    accentBg: 'bg-violet-50',
    accentBorder: 'border-violet-100',
    title: 'Quản lý rủi ro tốt hơn',
    hook: 'Rủi ro không được quản lý là con đường nhanh nhất tới cháy tài khoản.',
    body:
      'Mỗi lệnh mở ra đều phải có một kịch bản rủi ro rõ ràng từ trước. Bằng cách xác định tỷ lệ rủi ro chấp nhận được, khoảng cách cắt lỗ và khối lượng vị thế phù hợp, máy tính giúp bạn khóa chặt mức thua lỗ tối đa trong ngưỡng an toàn — không để cảm xúc hay lòng tham quyết định thay bạn.',
    label: 'Rủi ro khóa mỗi lệnh',
    valueNum: 2,
    valuePrefix: '≤ ',
    valueSuffix: '%',
    tags: ['Tỷ lệ rủi ro', 'Khoảng cách cắt lỗ', 'Khối lượng vị thế'],
    stats: [
      { label: 'Cắt lỗ tối đa', value: '-2%' },
      { label: 'Tỷ lệ R:R', value: '1:2' },
      { label: 'Ngưỡng an toàn', value: '<2%' },
    ],
    bar: { label: 'Rủi ro phân bổ', pct: 2 },
    dataLeft: true,
  },
  {
    num: '03',
    icon: 'ri-line-chart-line',
    accent: '#7c3aed',
    accentBg: 'bg-violet-50',
    accentBorder: 'border-violet-100',
    title: 'Tối ưu chiến lược giao dịch',
    hook: 'Dữ liệu là thứ phân biệt nhà giao dịch chuyên nghiệp với tay mơ.',
    body:
      'Ngoài vai trò tính toán, máy tính Forex còn là công cụ phân tích dữ liệu giao dịch. Bạn có thể đánh giá hiệu quả chiến lược qua tỷ lệ thắng, tỷ suất sinh lời và mức sụt giảm tối đa, từ đó nhận diện điểm mạnh, điểm yếu và tinh chỉnh để đạt lợi nhuận ổn định trong dài hạn.',
    label: 'Tỷ lệ thắng trung bình',
    valueNum: 62,
    valuePrefix: '',
    valueSuffix: '%',
    tags: ['Tỷ lệ thắng', 'Tỷ suất sinh lời', 'Mức sụt giảm tối đa'],
    stats: [
      { label: 'Tỷ suất sinh lời', value: '1.8' },
      { label: 'Drawdown', value: '12%' },
      { label: 'Profit factor', value: '2.4' },
    ],
    bar: { label: 'Tỷ lệ thắng', pct: 62 },
    dataLeft: false,
  },
];

function CountUp({
  to,
  prefix,
  suffix,
  start,
  duration = 1400,
}: {
  to: number;
  prefix: string;
  suffix: string;
  start: boolean;
  duration?: number;
}) {
  const [val, setVal] = useState(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!start) return;
    const t0 = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal((to - 0) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [start, to, duration]);

  return (
    <span>
      {prefix}
      {Math.round(val)}
      {suffix}
    </span>
  );
}

function TiltCard({
  accent,
  accentBg,
  accentBorder,
  children,
}: {
  accent: string;
  accentBg: string;
  accentBorder: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});
  const [spot, setSpot] = useState<{ x: number; y: number; o: number }>({
    x: 50,
    y: 50,
    o: 0,
  });

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const rx = (0.5 - py) * 8;
    const ry = (px - 0.5) * 10;
    setStyle({
      transform: `perspective(1000px) rotateX(${rx}deg) rotateY(${ry}deg) scale(1.02)`,
    });
    setSpot({ x: px * 100, y: py * 100, o: 1 });
  };

  const handleLeave = () => {
    setStyle({
      transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)',
      transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
    });
    setSpot((s) => ({ ...s, o: 0 }));
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={`group relative flex h-96 w-full flex-col items-center justify-center overflow-hidden rounded-3xl border px-8 shadow-lg transition-shadow duration-500 lg:w-1/2 ${accentBg} ${accentBorder}`}
      style={style}
    >
      {/* Cursor spotlight */}
      <div
        className="pointer-events-none absolute inset-0 transition-opacity duration-500"
        style={{
          opacity: spot.o,
          background: `radial-gradient(340px circle at ${spot.x}% ${spot.y}%, ${accent}26, transparent 70%)`,
        }}
      />
      {/* Soft ambient glow on hover */}
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background: `radial-gradient(600px circle at center, ${accent}14, transparent 70%)`,
        }}
      />
      <div className="relative flex w-full flex-col items-center">
        {children}
      </div>
    </div>
  );
}

export default function ToolMatrix() {
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="tools"
      className="relative overflow-hidden bg-slate-50 px-4 py-20 md:px-6 md:py-28"
    >
      {/* Decorative background glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="animate-float absolute -left-24 top-10 h-72 w-72 rounded-full bg-violet-100/40 blur-3xl" />
        <div
          className="animate-float absolute -right-16 bottom-0 h-80 w-80 rounded-full bg-violet-100/40 blur-3xl"
          style={{ animationDelay: '-3s' }}
        />
        <div
          className="animate-float absolute left-1/2 top-1/2 h-64 w-64 rounded-full bg-violet-100/40 blur-3xl"
          style={{ animationDelay: '-1.5s' }}
        />
      </div>

      <div className="relative mx-auto max-w-6xl">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 lg:text-5xl">
            Tại sao sử dụng máy tính Forex?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-500">
            Từ tính toán chính xác đến quản trị rủi ro và tối ưu chiến lược — mọi
            quyết định giao dịch đều trở nên sáng suốt và có cơ sở dữ liệu hơn.
          </p>
        </div>

        {/* Alternating Z-pattern rows */}
        <div className="mt-16 flex flex-col gap-24">
          {REASONS.map((reason, i) => {
            const textBlock = (
              <div className="w-full lg:w-1/2">
                <div className="flex items-center gap-3">
                  <span
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
                    style={{
                      backgroundColor: `${reason.accent}1A`,
                      color: reason.accent,
                    }}
                  >
                    <i className={`${reason.icon} text-lg`}></i>
                  </span>
                  <span className="font-mono text-xs font-semibold text-slate-400">
                    {reason.num}
                  </span>
                </div>

                <h3 className="mt-5 text-2xl font-bold text-slate-900">
                  {reason.title}
                </h3>
                <p
                  className="mt-2 font-semibold"
                  style={{ color: reason.accent }}
                >
                  {reason.hook}
                </p>
                <p className="mt-4 leading-loose text-slate-600">
                  {reason.body}
                </p>

                <div className="mt-6 flex flex-wrap gap-2">
                  {reason.tags.map((tag) => (
                    <span
                      key={tag}
                      className="cursor-pointer whitespace-nowrap rounded-full bg-slate-200 px-4 py-2 text-sm text-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                      style={{
                        ['--tw-shadow-color' as string]: `${reason.accent}33`,
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            );

            const dataBlock = (
              <TiltCard
                accent={reason.accent}
                accentBg={reason.accentBg}
                accentBorder={reason.accentBorder}
              >
                <span className="mb-3 font-medium text-slate-500">
                  {reason.label}
                </span>
                <span
                  className="font-display text-7xl font-black leading-none"
                  style={{ color: reason.accent }}
                >
                  <CountUp
                    to={reason.valueNum}
                    prefix={reason.valuePrefix}
                    suffix={reason.valueSuffix}
                    start={inView}
                  />
                </span>

                <div className="mt-6 h-px w-full bg-slate-200" />

                <div className="mt-5 grid w-full grid-cols-3 gap-2">
                  {reason.stats.map((stat) => (
                    <div key={stat.label} className="text-center">
                      <p className="text-xs text-slate-500">{stat.label}</p>
                      <p className="mt-1 whitespace-nowrap text-sm font-semibold text-slate-800">
                        {stat.value}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 w-full">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{reason.bar.label}</span>
                    <span className="font-semibold">{reason.bar.pct}%</span>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full transition-all duration-1000 ease-out"
                      style={{
                        width: inView ? `${reason.bar.pct}%` : '0%',
                        backgroundColor: reason.accent,
                      }}
                    />
                  </div>
                </div>
              </TiltCard>
            );

            return (
              <div
                key={reason.num}
                className="flex flex-col items-center gap-8 lg:flex-row lg:gap-12"
                style={{
                  transform: inView
                    ? 'translateY(0)'
                    : 'translateY(32px)',
                  opacity: inView ? 1 : 0,
                  transition: `transform 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${i * 140}ms, opacity 0.7s ease-out ${i * 140}ms`,
                }}
              >
                {reason.dataLeft ? dataBlock : textBlock}
                {reason.dataLeft ? textBlock : dataBlock}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}